import { extractZodIssues } from '@/lib/utils/zodErrorHelpers'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import type { MassClrSelectionFilters } from '@/lib/utils/populationFilters'
import {
  isMassClrErrorType,
  MASS_CLR_RUN_SCOPES,
  type ClrSelectableRiskClassCode,
  type MassClrErrorType,
  type MassClrRunScope,
  type MassClrSelectionSortField,
} from '@repo/constants'
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Mass CLR queries and mutations (FUZE-612), over the real backend
// (/credit/mass-credit-limit-reviews). Response types are inferred from the hono router, like
// every other credit hook.
// ─────────────────────────────────────────────────────────────────────────────────────────────

export { MASS_CLR_RUN_SCOPES }

export const massClrRunsSearchSchema = z.object({
  scope: z.enum(MASS_CLR_RUN_SCOPES).optional(),
})
export type MassClrRunsSearch = z.infer<typeof massClrRunsSearchSchema>

/** The population filters as the create/preview endpoints expect them — see `lib/utils/populationFilters`. */
export type { MassClrSelectionFilters }

export type MassClrSelectionView = {
  page: number
  pageSize: number
  sortField?: MassClrSelectionSortField
  sortDesc?: boolean
}

async function listMassClrRuns(scope: MassClrRunScope, signal?: AbortSignal) {
  const res = await getApi().credit['mass-credit-limit-reviews'].$get(
    { query: stringifyValues({ scope }) },
    { init: { signal } },
  )
  return await res.json()
}

export type MassClrRunRow = Awaited<ReturnType<typeof listMassClrRuns>>['rows'][number]

export function useMassClrRuns(scope: MassClrRunScope) {
  return useQuery({
    queryKey: ['massClrRuns', scope],
    queryFn: ({ signal }) => listMassClrRuns(scope, signal),
  })
}

async function getMassClrRun(runId: number, signal?: AbortSignal) {
  const res = await getApi().credit['mass-credit-limit-reviews'][':id'].$get(
    { param: { id: String(runId) } },
    { init: { signal } },
  )
  return await res.json()
}

export type MassClrRunDetail = Awaited<ReturnType<typeof getMassClrRun>>
export type MassClrChainPerson = MassClrRunDetail['chain']['approvers'][number]

export function massClrRunQueryOptions(runId: number) {
  return queryOptions({
    queryKey: ['massClrRun', runId],
    queryFn: ({ signal }) => getMassClrRun(runId, signal),
  })
}

export function useMassClrRun(runId: number, enabled = true) {
  return useQuery({ ...massClrRunQueryOptions(runId), enabled })
}

/**
 * Filters for the line list. All optional: with none set the endpoint returns every line, which is
 * what the preparer's workbench relies on. The approver pane narrows to the exceptions instead —
 * nobody reads 10k lines, they read the deviations. The population filters are the Customers
 * list's, answered by the account's live attributes (the run page's filter panel).
 */
export type MassClrLinesQuery = Partial<MassClrSelectionFilters> & {
  onlyDeviations?: boolean
  onlyFlagged?: boolean
  page?: number
  pageSize?: number
}

async function getMassClrRunLines(
  runId: number,
  query: MassClrLinesQuery = {},
  signal?: AbortSignal,
) {
  const res = await getApi().credit['mass-credit-limit-reviews'][':id'].lines.$get(
    { param: { id: String(runId) }, query: stringifyValues(query) },
    { init: { signal } },
  )
  return await res.json()
}

export type MassClrLine = Awaited<ReturnType<typeof getMassClrRunLines>>['lines'][number]

export function useMassClrRunLines(runId: number, enabled = true, query: MassClrLinesQuery = {}) {
  return useQuery({
    queryKey: ['massClrRunLines', runId, query],
    queryFn: ({ signal }) => getMassClrRunLines(runId, query, signal),
    enabled,
  })
}

async function getMassClrSelectionPreview(
  filters: MassClrSelectionFilters,
  view: MassClrSelectionView,
  signal?: AbortSignal,
) {
  // POST, not GET: the ERP filter takes a pasted Excel column, and a JSON-encoded array of more
  // than ~815 ERPs overflowed node's 16KB header limit and came back 431. Sent as a body instead,
  // so no `stringifyValues` and real arrays/numbers cross the wire.
  const res = await getApi().credit['mass-credit-limit-reviews']['selection-preview'].$post(
    {
      json: {
        ...filters,
        page: view.page,
        pageSize: view.pageSize,
        sort: view.sortField ? { id: view.sortField, desc: view.sortDesc ?? false } : undefined,
      },
    },
    { init: { signal } },
  )
  return await res.json()
}

/**
 * Whether the viewer may prepare mass runs at all. The answer does not depend on which accounts a
 * run holds, which is what lets the New Mass Review screen disable its actions up front instead of
 * letting the POST fail into a toast.
 */
export function useMassClrPreparerEligibility() {
  return useQuery({
    queryKey: ['massClrPreparerEligibility'],
    queryFn: async ({ signal }) => {
      const res = await getApi().credit['mass-credit-limit-reviews'].eligibility.$get(undefined, {
        init: { signal },
      })
      return await res.json()
    },
  })
}

export type MassClrSelectionPreview = Awaited<ReturnType<typeof getMassClrSelectionPreview>>
export type MassClrSelectionRow = MassClrSelectionPreview['rows'][number]

export function useMassClrSelectionPreview(
  filters: MassClrSelectionFilters,
  view: MassClrSelectionView,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['massClrSelectionPreview', filters, view],
    queryFn: ({ signal }) => getMassClrSelectionPreview(filters, view, signal),
    enabled,
    // Filter/page/sort changes swap the query key — keep the previous page on screen instead of
    // flashing a loading state.
    placeholderData: keepPreviousData,
  })
}

export type MassClrLinePatch =
  | {
      kind: 'deviate'
      proposedCreditLimit?: number
      proposedRiskClass?: ClrSelectableRiskClassCode
      reasonForDeviation: string
    }
  | { kind: 'reset' }
  | { kind: 'remove' }
  | { kind: 'exclude' }
  | { kind: 'restore' }

/** Reads the translatable mass business-rule key off a thrown error body, if any. Exported so
 *  the shared inbox decision hook can translate mass gate errors too. */
export function extractMassClrErrorType(error: unknown): MassClrErrorType | undefined {
  const issues = extractZodIssues(error)
  const type = (
    issues?.find((issue) => issue.code === 'custom')?.params as { type?: unknown } | undefined
  )?.type
  return isMassClrErrorType(type) ? type : undefined
}

function useMassClrErrorToast() {
  const { t } = useTranslation('credit')
  return (error: unknown) => {
    const errorType = extractMassClrErrorType(error)
    toast.error(errorType ? t(`clr.errors.${errorType}`) : t('massClr.toasts.error'))
  }
}

function useInvalidateRun() {
  const queryClient = useQueryClient()
  return (runId?: number) => {
    queryClient.invalidateQueries({ queryKey: ['massClrRuns'] })
    if (runId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ['massClrRun', runId] })
      queryClient.invalidateQueries({ queryKey: ['massClrRunLines', runId] })
    }
  }
}

export function useCreateMassClrRun() {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateRun()
  const { t } = useTranslation('credit')
  const onError = useMassClrErrorToast()

  return useMutation({
    mutationFn: async (input: { name: string; filters: MassClrSelectionFilters }) => {
      const res = await getApi().credit['mass-credit-limit-reviews'].$post({ json: input })
      return await res.json()
    },
    onSuccess: (created) => {
      invalidate(created.id)
      // Creation re-scopes future previews: the run's accounts are now "already in review"
      // only once submitted, but exclusion counts and drafts both moved — refetch to be safe.
      queryClient.invalidateQueries({ queryKey: ['massClrSelectionPreview'] })
      toast.success(t('massClr.toasts.draftCreated'))
    },
    onError,
  })
}

export function usePatchMassClrLines(runId: number) {
  const invalidate = useInvalidateRun()
  const { t } = useTranslation('credit')
  const onError = useMassClrErrorToast()

  return useMutation({
    mutationFn: async (input: { lineIds: number[]; patch: MassClrLinePatch }) => {
      const res = await getApi().credit['mass-credit-limit-reviews'][':id'].lines.$patch({
        param: { id: String(runId) },
        json: input,
      })
      return await res.json()
    },
    onSuccess: (result, input) => {
      invalidate(runId)
      const count = result.patchedCount
      if (input.patch.kind === 'deviate') {
        toast.success(t('massClr.toasts.deviationApplied', { count }))
      } else if (input.patch.kind === 'reset') {
        toast.success(t('massClr.toasts.resetToRecommendation', { count }))
      } else if (input.patch.kind === 'remove') {
        toast.warning(t('massClr.toasts.removedFromRun', { count }))
      } else if (input.patch.kind === 'exclude') {
        toast.warning(t('massClr.toasts.excluded', { count }))
      } else if (input.patch.kind === 'restore') {
        toast.success(t('massClr.toasts.restored', { count }))
      }
      // Acknowledgement changes are silent — they happen in bulk and per row constantly.
    },
    onError,
  })
}

export function useCancelMassClrRun() {
  const invalidate = useInvalidateRun()
  const { t } = useTranslation('credit')
  const onError = useMassClrErrorToast()

  return useMutation({
    mutationFn: async (runId: number) => {
      const res = await getApi().credit['mass-credit-limit-reviews'][':id'].cancel.$post({
        param: { id: String(runId) },
      })
      return await res.json()
    },
    onSuccess: (result, runId) => {
      invalidate(runId)
      toast.warning(t('massClr.toasts.draftCancelled', { count: result.releasedCount }))
    },
    onError,
  })
}

export function useSubmitMassClrRun(runId: number) {
  const invalidate = useInvalidateRun()
  const { t } = useTranslation('credit')
  const onError = useMassClrErrorToast()

  return useMutation({
    mutationFn: async () => {
      const res = await getApi().credit['mass-credit-limit-reviews'][':id'].submit.$post({
        param: { id: String(runId) },
      })
      return await res.json()
    },
    onSuccess: (result) => {
      invalidate(runId)
      toast.success(t('massClr.toasts.submitted', { count: result.submittedCount }))
    },
    onError,
  })
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Line comments (FUZE-616)
// ─────────────────────────────────────────────────────────────────────────────────────────────

async function getMassClrLineComments(runId: number, signal?: AbortSignal) {
  const res = await getApi().credit['mass-credit-limit-reviews'][':id']['line-comments'].$get(
    { param: { id: String(runId) } },
    { init: { signal } },
  )
  return await res.json()
}

export type MassClrLineComment = Awaited<
  ReturnType<typeof getMassClrLineComments>
>['comments'][number]

export function useMassClrLineComments(runId: number, enabled = true) {
  return useQuery({
    queryKey: ['massClrLineComments', runId],
    queryFn: ({ signal }) => getMassClrLineComments(runId, signal),
    enabled,
  })
}

/** Batch-writes ONE comment onto many lines — the "filter, select, drop a note" gesture, for the
 *  preparer on an editable run and for the approver on a submitted one. Append-only server-side. */
export function useAddMassClrLineComments(runId: number) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('credit')
  const onError = useMassClrErrorToast()

  return useMutation({
    mutationFn: async (input: { lineIds: number[]; comment: string }) => {
      const res = await getApi().credit['mass-credit-limit-reviews'][':id']['line-comments'].$post({
        param: { id: String(runId) },
        json: input,
      })
      return await res.json()
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['massClrLineComments', runId] })
      toast.success(t('massClr.toasts.commented', { count: result.commentedCount }))
    },
    onError,
  })
}
